import React, { useEffect, useState } from 'react';
import { superAdminApi } from '@/services/superAdminApi';
import { Switch } from '@/components/ui/switch';
import { toast } from 'sonner';
import { Loader2, Eye, EyeOff, ArrowUp, ArrowDown, GripVertical } from 'lucide-react';

export default function BranchesPage() {
  const [branches, setBranches] = useState([]);
  const [loading, setLoading] = useState(true);
  const [savingKey, setSavingKey] = useState(null);
  const [savingOrder, setSavingOrder] = useState(false);
  const [dragKey, setDragKey] = useState(null);
  const [overKey, setOverKey] = useState(null);

  const load = async () => {
    try {
      const { data } = await superAdminApi.listBranches();
      setBranches(data?.branches || []);
    } catch (e) {
      toast.error('Branchen konnten nicht geladen werden');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const toggle = async (key, enabled) => {
    setSavingKey(key);
    setBranches((prev) => prev.map((b) => (b.key === key ? { ...b, enabled } : b)));
    try {
      await superAdminApi.setBranchEnabled(key, enabled);
      toast.success(enabled ? 'Branche freigeschaltet' : 'Branche deaktiviert');
    } catch (e) {
      setBranches((prev) => prev.map((b) => (b.key === key ? { ...b, enabled: !enabled } : b)));
      toast.error('Speichern fehlgeschlagen');
    } finally {
      setSavingKey(null);
    }
  };

  const persistOrder = async (ordered) => {
    setSavingOrder(true);
    try {
      await superAdminApi.setBranchOrder(ordered.map((b) => b.key));
      toast.success('Reihenfolge gespeichert');
    } catch (e) {
      toast.error('Reihenfolge konnte nicht gespeichert werden');
      load();
    } finally {
      setSavingOrder(false);
    }
  };

  const move = (index, dir) => {
    const target = index + dir;
    if (target < 0 || target >= branches.length) return;
    const next = [...branches];
    [next[index], next[target]] = [next[target], next[index]];
    setBranches(next);
    persistOrder(next);
  };

  const handleDrop = (targetKey) => {
    const from = branches.findIndex((b) => b.key === dragKey);
    const to = branches.findIndex((b) => b.key === targetKey);
    setDragKey(null);
    setOverKey(null);
    if (from === -1 || to === -1 || from === to) return;
    const next = [...branches];
    const [moved] = next.splice(from, 1);
    next.splice(to, 0, moved);
    setBranches(next);
    persistOrder(next);
  };

  if (loading) {
    return <div className="flex justify-center py-24"><Loader2 className="h-6 w-6 animate-spin text-indigo-500" /></div>;
  }

  const enabledCount = branches.filter((b) => b.enabled).length;

  return (
    <div data-testid="sa-branches-page">
      <div className="mb-8 flex items-start justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-slate-900">Branchen freischalten</h1>
          <p className="mt-1 text-sm text-slate-500">
            Nur freigeschaltete Branchen erscheinen auf der öffentlichen Auswahlseite (/branchen).
            Mit den Pfeilen oder per Ziehen änderst du die Reihenfolge der Dienstleistungen.
            Aktuell sichtbar: <strong>{enabledCount} / {branches.length}</strong>.
          </p>
        </div>
        {savingOrder && <Loader2 className="mt-1 h-4 w-4 shrink-0 animate-spin text-indigo-500" />}
      </div>

      <div className="flex flex-col gap-3">
        {branches.map((b, index) => (
          <div
            key={b.key}
            data-testid={`sa-branch-row-${b.key}`}
            draggable={!savingOrder}
            onDragStart={() => setDragKey(b.key)}
            onDragEnd={() => { setDragKey(null); setOverKey(null); }}
            onDragOver={(e) => { e.preventDefault(); if (overKey !== b.key) setOverKey(b.key); }}
            onDrop={() => handleDrop(b.key)}
            className={`flex items-center gap-4 rounded-2xl border bg-white p-4 shadow-sm transition-all ${
              dragKey === b.key ? 'opacity-40' : ''
            } ${overKey === b.key && dragKey && dragKey !== b.key ? 'border-indigo-400 ring-2 ring-indigo-200' : 'border-slate-200'}`}
          >
            <GripVertical
              size={18}
              data-testid={`sa-branch-drag-${b.key}`}
              className="shrink-0 cursor-grab text-slate-300 hover:text-slate-500 active:cursor-grabbing"
            />
            <div className="flex flex-col">
              <button
                type="button"
                data-testid={`sa-branch-up-${b.key}`}
                disabled={index === 0 || savingOrder}
                onClick={() => move(index, -1)}
                className="rounded-md p-1 text-slate-400 transition-colors hover:bg-slate-100 hover:text-slate-700 disabled:opacity-30"
                aria-label="Nach oben"
              >
                <ArrowUp size={16} />
              </button>
              <button
                type="button"
                data-testid={`sa-branch-down-${b.key}`}
                disabled={index === branches.length - 1 || savingOrder}
                onClick={() => move(index, 1)}
                className="rounded-md p-1 text-slate-400 transition-colors hover:bg-slate-100 hover:text-slate-700 disabled:opacity-30"
                aria-label="Nach unten"
              >
                <ArrowDown size={16} />
              </button>
            </div>
            <span className="w-5 shrink-0 text-center text-sm font-semibold text-slate-400">{index + 1}</span>
            <div className="h-14 w-14 shrink-0 overflow-hidden rounded-xl border border-slate-100 bg-slate-50 flex items-center justify-center">
              <img src={b.logo_url} alt={b.name} className="max-h-12 w-auto object-contain" />
            </div>
            <div className="min-w-0 flex-1">
              <div className="flex items-center gap-2">
                <span className="font-semibold text-slate-900 truncate">{b.name}</span>
                <span
                  className="inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[11px] font-medium"
                  style={{ backgroundColor: `${b.accent_color}1a`, color: b.accent_color }}
                >
                  {b.enabled ? <Eye size={11} /> : <EyeOff size={11} />}
                  {b.enabled ? 'Sichtbar' : 'Versteckt'}
                </span>
              </div>
              <p className="mt-0.5 text-xs text-slate-500 line-clamp-2">{b.description}</p>
            </div>
            <Switch
              data-testid={`sa-branch-toggle-${b.key}`}
              checked={b.enabled}
              disabled={savingKey === b.key}
              onCheckedChange={(v) => toggle(b.key, v)}
            />
          </div>
        ))}
      </div>
    </div>
  );
}
